import { useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Minus, Plus } from 'lucide-react';
import MainContainer from '../../components/containers/MainContainer';
import BaseContainer from '../../components/containers/BaseContainer';
import Bar from '../../components/elements/Bar';
import List from '../../components/elements/List';
import { Switch } from '../../components/ui/Switch';
import { Button } from '../../components/ui/Button';
import ConfirmDeleteDialog from '../../components/ui/ConfirmDeleteDialog';
import { SpinnerOverlay } from '../../components/ui/Spinner';
import PickClassificationModal from '../../components/modals/PickClassificationModal';
import AddApproverDialog from './components/AddApproverDialog';
import { CLASSIFICATION_TREE_URL } from './constants';
import {
  useApprovers,
  useCompanyEmployees,
  useCreateApprover,
  useCreateExemption,
  useDeleteApprover,
  useDeleteExemption,
  useExemptions,
  useToggleApproval,
} from './hooks/useApprovalSettings';
import { useApprovalContext } from './hooks/useApprovals';

const employeeName = (e: any): string =>
  e?.title || e?.name || e?.user_name || e?.full_name || e?.email || String(e?.id);

const SettingsPage = ({ children }: { children: ReactNode }) => (
  <MainContainer>
    <BaseContainer fullHeight margin width={'100%' as any}>
      <BaseContainer margin width={'100%' as any}>
        {children}
      </BaseContainer>
    </BaseContainer>
  </MainContainer>
);

function ApprovalSettings() {
  const companyId = useSelector((state: any) => state.auth.current_company);
  const toggle = useToggleApproval(companyId);
  const context = useApprovalContext();
  return (
    <SettingsPage>
      <Bar text="Nastavení schvalování" />
      <div className="px-6 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm text-neutral-900">Vyžadovat schvalování verzí</div>
          <div className="text-xs text-neutral-500">
            Nové a upravené firemní verze musí před použitím schválit schvalovatel.
          </div>
        </div>
        <Switch
          checked={!!context.data?.enabled}
          disabled={toggle.isLoading || context.isFetching || !context.data}
          onCheckedChange={(enabled) => toggle.mutate(enabled)}
          data-testid="approval-toggle-switch"
        />
      </div>
    </SettingsPage>
  );
}

export function ApprovalApprovers() {
  const [open, setOpen] = useState(false);
  const { data: approvers, isLoading, isError } = useApprovers();
  const { data: employees } = useCompanyEmployees();
  const create = useCreateApprover();
  const remove = useDeleteApprover();
  const names = new Map((employees ?? []).map((e: any) => [String(e.id), employeeName(e)]));
  const rows = (approvers ?? []).map((a) => ({
    id: a.id,
    name: (names.get(String(a.employee_id)) as string) || String(a.employee_id),
    value: a.classification_id ? `Klasifikace: ${a.classification_id}` : 'Globální schvalovatel',
  }));
  return (
    <SettingsPage>
      <Bar
        text="Schvalovatelé"
        actions={
          <Button
            variant="ghost"
            size="sm"
            className="mr-2"
            onClick={() => setOpen(true)}
            data-testid="approval-add-approver-btn"
          >
            <Plus className="h-4 w-4" />
            Přidat
          </Button>
        }
      />
      {isLoading ? (
        <SpinnerOverlay />
      ) : isError ? (
        <div className="p-6 text-sm">Schvalovatele se nepodařilo načíst.</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm">Žádní schvalovatelé.</div>
      ) : (
        <List
          data={rows}
          canEdit
          actions={[
            (id) => (
              <ConfirmDeleteDialog
                title="Opravdu odebrat schvalovatele?"
                onConfirmAsync={() => remove.mutateAsync(String(id))}
              >
                <Minus className="h-4 w-4" />
              </ConfirmDeleteDialog>
            ),
          ]}
        />
      )}
      {open && (
        <AddApproverDialog
          open
          onClose={() => setOpen(false)}
          submitting={create.isLoading}
          onSubmit={(payload) => create.mutate(payload, { onSuccess: () => setOpen(false) })}
        />
      )}
    </SettingsPage>
  );
}

export function ApprovalExemptions() {
  const [open, setOpen] = useState(false);
  const { data: exemptions, isLoading, isError } = useExemptions();
  const create = useCreateExemption();
  const remove = useDeleteExemption();
  const rows = (exemptions ?? []).map((e) => ({
    id: e.id,
    name: 'Klasifikace',
    value: e.classification_id ?? '—',
  }));
  return (
    <SettingsPage>
      <Bar
        text="Výjimky ze schvalování"
        actions={
          <Button
            variant="ghost"
            size="sm"
            className="mr-2"
            onClick={() => setOpen(true)}
            data-testid="approval-add-exemption-btn"
          >
            <Plus className="h-4 w-4" />
            Přidat
          </Button>
        }
      />
      {isLoading ? (
        <SpinnerOverlay />
      ) : isError ? (
        <div className="p-6 text-sm">Výjimky se nepodařilo načíst.</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm">Žádné výjimky ze schvalování.</div>
      ) : (
        <List
          data={rows}
          canEdit
          actions={[
            (id) => (
              <ConfirmDeleteDialog
                title="Opravdu zrušit výjimku?"
                onConfirmAsync={() => remove.mutateAsync(String(id))}
              >
                <Minus className="h-4 w-4" />
              </ConfirmDeleteDialog>
            ),
          ]}
        />
      )}
      {open && (
        <PickClassificationModal
          visible
          url={CLASSIFICATION_TREE_URL}
          onClose={() => setOpen(false)}
          onSubmit={(item: any) => {
            if (!create.isLoading)
              create.mutate({ classification_id: item.id }, { onSuccess: () => setOpen(false) });
          }}
        />
      )}
    </SettingsPage>
  );
}

export default ApprovalSettings;
